import { useEffect, useState } from "react"
import { Badge, Card, Offcanvas, Spinner, Table } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import moment from "moment"
import { APIService } from "@/common/API"

interface OrderTracePanelProps {
  orderNumber: string
  organizationId: string
  show: boolean
  onHide: () => void
}

interface Trace {
  resolvedType: string | null
  matchedNumber: string
  organizationId: string
  customer?: { code: string; name: string } | null
  invoices: any[]
  trips: any[]
  dispatches: any[]
}

const TYPE_LABEL: Record<string, string> = {
  invoice: "Factura",
  trip: "Viaje",
  dispatch: "Orden de despacho",
}

const OrderTracePanel = ({ orderNumber, organizationId, show, onHide }: OrderTracePanelProps) => {
  const { t } = useTranslation("global")
  const [isLoading, setIsLoading] = useState(false)
  const [trace, setTrace] = useState<Trace | null>(null)

  useEffect(() => {
    if (!show || !orderNumber) return
    let active = true
    const load = async () => {
      setIsLoading(true)
      setTrace(null)
      try {
        const qs = `orderNumber=${encodeURIComponent(orderNumber)}&organizationId=${encodeURIComponent(organizationId)}`
        const res = await APIService.getAction("Dispatch/OrderTrace", qs)
        if (active && res.data.status === "ok") {
          setTrace(res.data.response)
        }
      } finally {
        if (active) setIsLoading(false)
      }
    }
    load()
    return () => { active = false }
  }, [show, orderNumber, organizationId])

  const hasData = trace && trace.resolvedType

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      backdropClassName="order-trace-backdrop"
      style={{ width: 560, maxWidth: "100%", zIndex: 1060 }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>
          <i className="fas fa-sitemap me-2" />
          {t("Documentos relacionados")}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        {isLoading ? (
          <div className="text-center py-5"><Spinner animation="border" /></div>
        ) : !hasData ? (
          <div className="text-center text-muted py-5">
            <i className="fas fa-folder-open fa-2x mb-3 d-block" />
            {t("Sin documentos relacionados")}
          </div>
        ) : (
          <>
            <div className="mb-4">
              <h5 className="mb-1">
                {trace!.matchedNumber}{" "}
                <Badge bg="info">{t(TYPE_LABEL[trace!.resolvedType!] || trace!.resolvedType!)}</Badge>
              </h5>
              {trace!.customer && (
                <div className="text-muted small">
                  <i className="fas fa-user me-1" />
                  {t("Cliente")}: {trace!.customer.name || trace!.customer.code}
                  {trace!.customer.code && <span className="ms-1">({trace!.customer.code})</span>}
                </div>
              )}
            </div>

            <section className="mb-4">
              <h6 className="text-uppercase text-muted small fw-bold">
                <i className="fas fa-file-invoice-dollar me-1" />{t("Factura")}
              </h6>
              {trace!.invoices.length === 0 ? (
                <p className="text-muted small mb-0">{t("Sin facturas")}</p>
              ) : (
                <Table size="sm" className="small mb-0">
                  <thead><tr>
                    <th>{t("Factura")}</th><th>{t("Fecha")}</th>
                    <th className="text-end">{t("Monto")}</th><th>{t("Status")}</th>
                  </tr></thead>
                  <tbody>
                    {trace!.invoices.map((inv: any) => (
                      <tr key={inv.invoiceNumber}>
                        <td>{inv.invoiceNumber}</td>
                        <td className="text-nowrap">{moment(inv.date).format("DD/MM/YY")}</td>
                        <td className="text-end">{Number(inv.amount).toLocaleString()}</td>
                        <td>{inv.invoiceStatus || inv.paid}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </section>

            <section className="mb-4">
              <h6 className="text-uppercase text-muted small fw-bold">
                <i className="fas fa-truck me-1" />{t("Viajes")}
              </h6>
              {trace!.trips.length === 0 ? (
                <p className="text-muted small mb-0">{t("Sin viajes")}</p>
              ) : (
                <Table size="sm" className="small mb-0">
                  <thead><tr>
                    <th>{t("Viaje")}</th><th>{t("Fecha")}</th>
                    <th>{t("Chofer")}</th><th>{t("Vehículo")}</th><th>{t("Dependencia")}</th>
                  </tr></thead>
                  <tbody>
                    {trace!.trips.map((trip: any) => (
                      <tr key={trip.tripNumber}>
                        <td>{trip.tripNumber}</td>
                        <td className="text-nowrap">{moment(trip.date).format("DD/MM/YY")}</td>
                        <td>{trip.driver}</td>
                        <td>{trip.vehicleName}</td>
                        <td>{trip.dependency}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </section>

            <section className="mb-2">
              <h6 className="text-uppercase text-muted small fw-bold">
                <i className="fas fa-dolly me-1" />{t("Órdenes de despacho")}
              </h6>
              {trace!.dispatches.length === 0 ? (
                <p className="text-muted small mb-0">{t("Sin órdenes de despacho")}</p>
              ) : (
                trace!.dispatches.map((d: any) => (
                  <Card key={d.conduceNumber} className="mb-3 border">
                    <Card.Header className="py-2 d-flex justify-content-between align-items-center small">
                      <span><strong>{t("Conduce")}:</strong> {d.conduceNumber}</span>
                      <span className="text-muted text-nowrap">{moment(d.dispatchDate).format("DD/MM/YY")}</span>
                    </Card.Header>
                    <Card.Body className="p-0">
                      <div className="px-3 py-1 small text-muted border-bottom">
                        {d.dependency}
                        {d.tripNumber && <span className="ms-2"><i className="fas fa-truck me-1" />{d.tripNumber}</span>}
                      </div>
                      {d.details.length === 0 ? (
                        <p className="text-muted small px-3 py-2 mb-0">{t("Sin artículos")}</p>
                      ) : (
                        <Table size="sm" className="small mb-0">
                          <thead><tr>
                            <th>{t("Artículo")}</th><th>{t("Descripción")}</th>
                            <th className="text-end">{t("Cantidad")}</th><th>{t("Almacén")}</th>
                          </tr></thead>
                          <tbody>
                            {d.details.map((item: any, i: number) => (
                              <tr key={`${d.conduceNumber}-${item.itemCode}-${i}`}>
                                <td className="text-nowrap">{item.itemCode}</td>
                                <td>{item.description}</td>
                                <td className="text-end text-nowrap">{Number(item.quantity).toLocaleString()} {item.unit}</td>
                                <td>{item.warehouse}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      )}
                    </Card.Body>
                  </Card>
                ))
              )}
            </section>
          </>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  )
}

export default OrderTracePanel
